import { notifyAnalytics } from '@/api/analytics/notifyAnalytics'
import { VALHALLA_ROUTE_ENDPOINT } from '@/api/clients/valhalla-client'
import { maxValhallaLocations } from '@/data/const'
import {
  QuickTripCheckReturn,
  type Valhalla_CostingModel,
  type Valhalla_Location,
  type Valhalla_RouteError,
  type Valhalla_RouteRes,
} from '@/types/valhalla_types'

/**
 * Fetches a route plan from the Valhalla routing service.
 *
 * Sends a request to the Valhalla routing API with the provided locations and costing model.
 * Returns a Valhalla route response if successful or an error response if there is a problem
 * with the request. If an error occurs during the fetch operation, it logs the error and
 * returns undefined.
 *
 * @param locations - An array of Valhalla_Location objects representing the waypoints of the route.
 * @param costing - The costing model to use for the route calculation. Defaults to "auto".
 * @returns A promise that resolves to either a Valhalla_RouteRes object if the route is successfully
 * fetched, a Valhalla_RouteError object if there is an error in the response, or undefined if an
 * exception occurs during the fetch operation.
 */
export async function fetchValhallaRoutePlan(
  locations: Valhalla_Location[],
  costing: Valhalla_CostingModel = 'auto',
): Promise<Valhalla_RouteRes | Valhalla_RouteError | undefined> {
  try {
    const json = {
      locations: locations,
      costing: costing,
    }

    const res = await fetch(`${VALHALLA_ROUTE_ENDPOINT}?json=${JSON.stringify(json)}`)
    const convertedResponse = await res.json()
    return convertedResponse
  } catch (error) {
    notifyAnalytics({ type: 'log_error', data: error })
    return
  }
}

export async function quickTripCheck(
  locations: Valhalla_Location[],
  costing: Valhalla_CostingModel = 'auto',
): Promise<QuickTripCheckReturn> {
  const response = await fetchValhallaLongTrip({locations, costing})
  if (response && Object.hasOwn(response, 'trip')) {
    return QuickTripCheckReturn.routable
  } else if (
    response &&
    Object.hasOwn(response, 'error_code') &&
    (response as Valhalla_RouteError).error_code === 170
  ) {
    return QuickTripCheckReturn.unconnected_regions
  } else {
    return QuickTripCheckReturn.not_routable
  }
}

export async function fetchValhallaLongTrip({
  locations,
  costing = 'auto',
}: {
  locations: Valhalla_Location[]
  costing?: Valhalla_CostingModel
}): Promise<Valhalla_RouteRes | Valhalla_RouteError | undefined> {
  
  // check if can be completes in a single valhalla call
  if (locations.length <= maxValhallaLocations) return await fetchValhallaRoutePlan(locations, costing)
  
  // figure out how many valhalla calls need to be made
  let start = 0
  let end = 0
  const arraysOfLocations: Valhalla_Location[][] = []
  do {    
    end = start + maxValhallaLocations
    arraysOfLocations.push(locations.slice(start, end))
    start = end - 1
  } while (end < locations.length)

  // make valhalla calls simultaneously
  const promises = arraysOfLocations.map((locationsArray) =>
    fetchValhallaRoutePlan(locationsArray, costing),
  )

  const resolvedPromises = await Promise.all(promises)

  // check all the promises resolved correctly
  if (resolvedPromises.some(res => res === undefined)) return // exit if any resolved to undefined
  if (resolvedPromises.some(res => res && 'error' in res)) return // exit if any resolved to a routing error

  // piece back together the
  let returnData: Valhalla_RouteRes = resolvedPromises[0] as Valhalla_RouteRes
  (resolvedPromises as Valhalla_RouteRes[]).forEach((tripRes:Valhalla_RouteRes, index) => {
    if (index === 0) returnData = tripRes
    else {
      // add legs to return data
      returnData.trip.legs.push(...tripRes.trip.legs)
      // add distance to summary
      returnData.trip.summary.length += tripRes.trip.summary.length
      // add time to summary
      returnData.trip.summary.time += tripRes.trip.summary.time
      // check if has ferry flag needs to be updated, ignore this step if the flag is already true
      if (!returnData.trip.summary.has_ferry && tripRes.trip.summary.has_ferry) returnData.trip.summary.has_ferry = true
    } 
  })

  // return data
  return returnData
}
